import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';

import RatingStars from '~components/rating-stars';
import { TextareaControlled } from '~components/forms/textarea';
import Button from '~components/button';
import Form from '~components/forms/form';
import NotificationsActions from '~redux/notifications';
import PromptService from '~services/prompt';
import { createReview, updateReview } from '~services/reviews';

const MAX_COMMENT_LENGTH = 200;

const CompactReviewForm = ({ 
  authorId, 
  receiverId, 
  onSubmitSuccess,
  score = 0,
  comment = '',
  reviewId,
  action = 'create'
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [rating, setRating] = useState(score);

  const validationSchema = Yup.object().shape({
    score: Yup.number().required().min(1).max(5),
    comment: Yup.string()
      .max(MAX_COMMENT_LENGTH)
      .noEmailInContent()
      .noPhoneInContent()
      .noUrlInContent(),
  });

  const methods = useForm({
    mode: 'onChange',
    defaultValues: {
      score: score,
      comment: comment,
    },
  });

  const handleRatingChange = (value) => {
    setRating(value);
    methods.setValue('score', value, { shouldValidate: true });
  };

  const handleSubmit = async (formData) => {
    setIsSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      
      const reviewData = {
        authorId,
        receiverId,
        score: rating,
        text: formData.comment,
        token
      };

      if (action === 'create') {
        await createReview(reviewData);
      } else {
        await updateReview({ ...reviewData, reviewId });
      }

      dispatch(
        NotificationsActions.addNotification(
          intl.formatMessage({ 
            id: 'containers.review.notification', 
            defaultMessage: 'Review saved successfully.' 
          })
        )
      );
      
      setIsSubmitted(true);
      if (onSubmitSuccess) {
        onSubmitSuccess();
      }
    } catch (err) {
      console.log('Could not submit review', err);
      PromptService.error(err);
      // dispatch(
      //   NotificationsActions.addNotification(
      //     intl.formatMessage({ 
      //       id: 'containers.review.error', 
      //       defaultMessage: 'Could not submit review' 
      //     }),
      //     'error'
      //   )
      // );
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = (formData) => {
    PromptService.threeWayConfirm({
      title: intl.formatMessage({ 
        id: 'containers.review.prompt.title', 
        defaultMessage: 'Are you sure?' 
      }),
      content: intl.formatMessage({
        id: 'containers.review.prompt.content',
        defaultMessage: 'Are you sure that you want to submit this review?'
      }),
      ok: intl.formatMessage({
        id: 'containers.review.prompt.ok',
        defaultMessage: "Yes, I'm sure"
      }),
      cancel: intl.formatMessage({
        id: 'containers.review.prompt.cancel',
        defaultMessage: "No, cancel"
      }),
    }).then((response) => {
      if (typeof response === 'undefined') {
        return;
      }
      handleSubmit(formData);
    });
  };

  return (
    <Form methods={methods} validationSchema={validationSchema} onSubmit={onSubmit}>
      <div className="c-compact-review">
        <div className="c-compact-review__stars">
          <RatingStars
            initialValue={rating}
            onChange={handleRatingChange}
          />
        </div>
        <div className="c-fieldset">
          <div className="c-compact-review__comment">
            <TextareaControlled
              name="comment"
              placeholder={intl.formatMessage({
                id: 'containers.review.comment.placeholder',
                defaultMessage: 'Write something about your cooperation',
              })}
              className="c-compact-review__textarea"
            />
          </div>
        </div>
        <div className="c-fieldset">
          <div className="c-modal__buttons">
            <Button
              type="submit"
              isLoading={isSubmitting}
              disabled={isSubmitting || !rating}
              className="c-btn c-btn--primary"
            >
              {intl.formatMessage({
                id: 'containers.review.save',
                defaultMessage: 'Save',
              })}
            </Button>
          </div>
        </div>
      </div>
    </Form>
  );
};

export default CompactReviewForm; 